import { useLandingEdit } from "./LandingEditContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Pencil, Save, X, User, Users, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface AdminEditToolbarProps {
  className?: string;
}

export function AdminEditToolbar({ className }: AdminEditToolbarProps) {
  const { 
    isEditMode, 
    setEditMode, 
    viewMode, 
    setViewMode, 
    isSaving,
    hasUnsavedChanges,
    saveAllChanges,
    cancelAllChanges,
  } = useLandingEdit();
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      await saveAllChanges();
      toast({
        title: "Changes saved",
        description: "Your content changes have been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Error saving",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    cancelAllChanges();
    toast({
      title: "Changes discarded",
      description: "Your unsaved changes have been discarded.",
    });
  };

  const handleToggleEditMode = () => {
    if (isEditMode && hasUnsavedChanges) {
      // Warn about unsaved changes
      cancelAllChanges();
    }
    setEditMode(!isEditMode);
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 left-1/2 -translate-x-1/2 z-50",
        "bg-card/95 backdrop-blur-md border shadow-lg rounded-lg",
        "px-4 py-3 flex items-center gap-4",
        className
      )}
      data-testid="admin-edit-toolbar"
    >
      {!isEditMode ? (
        <Button
          onClick={handleToggleEditMode}
          variant="default"
          className="gap-2"
          data-testid="button-edit-page"
        >
          <Pencil className="w-4 h-4" />
          Edit Page
        </Button>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-muted-foreground">View as:</Label>
            <div className="flex items-center gap-1 bg-muted rounded-md p-1">
              <Button
                size="sm"
                variant={viewMode === "anonymous" ? "default" : "ghost"}
                className="h-7 px-2 gap-1"
                onClick={() => setViewMode("anonymous")}
                disabled={hasUnsavedChanges}
                data-testid="button-view-anonymous"
              >
                <User className="w-3 h-3" />
                <span className="text-xs">Anonymous</span>
              </Button>
              <Button
                size="sm"
                variant={viewMode === "loggedIn" ? "default" : "ghost"}
                className="h-7 px-2 gap-1"
                onClick={() => setViewMode("loggedIn")}
                disabled={hasUnsavedChanges}
                data-testid="button-view-logged-in"
              >
                <Users className="w-3 h-3" />
                <span className="text-xs">Logged In</span>
              </Button>
            </div>
          </div>

          <div className="h-6 w-px bg-border" />

          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              variant="default"
              className="gap-2"
              data-testid="button-save-content"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </Button>
            <Button
              onClick={handleCancel}
              disabled={isSaving || !hasUnsavedChanges}
              variant="outline"
              className="gap-2"
              data-testid="button-cancel-edit"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
          </div>

          <div className="h-6 w-px bg-border" />

          <Button
            onClick={handleToggleEditMode}
            variant="ghost"
            size="sm"
            data-testid="button-exit-edit"
          >
            Exit Edit Mode
          </Button>

          {hasUnsavedChanges && (
            <span className="text-xs text-amber-500 font-medium">
              Unsaved changes
            </span>
          )}
        </>
      )}
    </div>
  );
}
